<script setup>
import { computed } from 'vue';

const props = defineProps({
  generatedAt: {
    type: Date,
    default: null,
  },
  isRefreshing: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['refresh']);

/**
 * When the figures were built, in the viewer's time. The date is only spelled out once it is no
 * longer today, so the common case stays as short as "3:42 PM".
 */
const label = computed(() => {
  const built = props.generatedAt;
  if (!built) {
    return '';
  }
  const time = built.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  if (built.toDateString() === new Date().toDateString()) {
    return time;
  }
  return `${built.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}, ${time}`;
});

const fullLabel = computed(() => (props.generatedAt
  ? props.generatedAt.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'medium' })
  : ''));
</script>

<template>
  <div :class="$style.wrapper">
    <span
      v-if="label"
      :class="$style.label"
      :title="fullLabel"
      aria-live="polite"
    >
      Last refreshed {{ label }}
    </span>
    <button
      type="button"
      :class="$style.button"
      :disabled="isRefreshing"
      @click="emit('refresh')"
    >
      <span :class="[$style.icon, isRefreshing ? $style.spinning : '']" aria-hidden="true">↻</span>
      {{ isRefreshing ? 'Refreshing…' : 'Refresh' }}
    </button>
  </div>
</template>

<style module>
.wrapper {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-end;
  gap: 4px 10px;
}
.label {
  font-size: var(--font-size-md);
  color: var(--faint-color);
}
.button {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: 1px solid var(--border-color);
  background: white;
  color: var(--primary-font-color);
  border-radius: var(--border-radius-xxl);
  padding: 4px 14px;
  font-size: var(--font-size-md);
  font-weight: 500;
  cursor: pointer;
  white-space: nowrap;
}
.button:hover:not(:disabled) {
  border-color: var(--pfs-red);
  color: var(--pfs-red);
}
.button:disabled {
  color: var(--faint-color);
  cursor: default;
}
.icon {
  display: inline-block;
  line-height: 1;
}
.spinning {
  animation: analytics-refresh-spin 0.9s linear infinite;
}
@keyframes analytics-refresh-spin {
  to { transform: rotate(360deg); }
}
@media (prefers-reduced-motion: reduce) {
  .spinning { animation: none; }
}
</style>
